import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { FilterBoxProps } from "../../../interfaces/queryBuilderInterfaces";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import ToggleSwitch from "../../../components/ToggleSwitch";
import { Button } from "../../../components/button/Button";
import { getTypeFromValue } from "../../../utils/util";

export interface FilterFormulaProp {
  toggleType: boolean;
  filter1Type: string;
  filter1Value: string;
  radioOption: string;
  filter2Type: string;
  filter2Value: string;
}

interface FilterFormulaProps {
  formula: FilterFormulaProp;
  setFormula?: (formula: FilterFormulaProp) => void;
}

export interface FilterFormulaRef {
  clearConditions: () => void;
}

const FilterFormula = forwardRef<FilterFormulaRef, FilterFormulaProps>(
  ({ formula, setFormula }, ref) => {
    const formulaKeys = ["=", "!=", "<", "<=", ">", ">="];

    const [selectedOption1, setSelectedOption1] = useState<string | null>(
      formula && getTypeFromValue(formula.filter1Type || "="),
    );
    const [selectedOption2, setSelectedOption2] = useState<string | null>(
      formula && getTypeFromValue(formula.filter2Type || "="),
    );
    const [selectedRadioOption, setSelectedRadioOption] = useState<string>(
      (formula && formula.radioOption) || "AND",
    );
    const [filter1Value, setFilter1Value] = useState<string>(
      (formula && formula.filter1Value) || "",
    );
    const [filter2Value, setFilter2Value] = useState<string>(
      (formula && formula.filter2Value) || "",
    );
    const [toggleSwitch, setToggleSwitch] = useState<boolean>(
      (formula && formula.toggleType) || false,
    );

    const formulaOptions = useMemo(() => {
      return formulaKeys.map((item) => ({
        value: item,
        label: item,
      }));
    }, []);

    const handleOption1Change = (value: string) => {
      setSelectedOption1(value);
      setFormula &&
        setFormula({
          toggleType: toggleSwitch,
          filter1Type: getValueOfType(value),
          filter1Value: filter1Value,
          radioOption: selectedRadioOption,
          filter2Type: getValueOfType(selectedOption2 || ""),
          filter2Value: filter2Value,
        });
    };

    const handleOption2Change = (value: string) => {
      setSelectedOption2(value);
      setFormula &&
        setFormula({
          toggleType: toggleSwitch,
          filter1Type: getValueOfType(selectedOption1 || ""),
          filter1Value: filter1Value,
          radioOption: selectedRadioOption,
          filter2Type: getValueOfType(value),
          filter2Value: filter2Value,
        });
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormula &&
        setFormula({
          toggleType: toggleSwitch,
          filter1Type: getValueOfType(selectedOption1 || ""),
          filter1Value: filter1Value,
          radioOption: event.target.value,
          filter2Type: getValueOfType(selectedOption2 || ""),
          filter2Value: filter2Value,
        });
      setSelectedRadioOption(event.target.value);
    };

    const handleValue1Change = (value: string) => {
      setFormula &&
        setFormula({
          toggleType: toggleSwitch,
          filter1Type: getValueOfType(selectedOption1 || "="),
          filter1Value: value,
          radioOption: selectedRadioOption,
          filter2Type: getValueOfType(selectedOption2 || ""),
          filter2Value: filter2Value,
        });
      setFilter1Value(value);
    };

    const handleValue2Change = (value: string) => {
      setFormula &&
        setFormula({
          toggleType: toggleSwitch,
          filter1Type: getValueOfType(selectedOption1 || ""),
          filter1Value: filter1Value,
          radioOption: selectedRadioOption,
          filter2Type: getValueOfType(selectedOption2 || "="),
          filter2Value: value,
        });
      setFilter2Value(value);
    };

    const getValueOfType = (type: string) => {
      if (!type) return "EqualTo";
      switch (type) {
        case "=":
          return "EqualTo";
        case "!=":
          return "NotEqualTo";
        case "<":
          return "LessThan";
        case "<=":
          return "LessThanOrEqualTo";
        case ">":
          return "GreaterThan";
        case ">=":
          return "GreaterThanOrEqualTo";
        default:
          return "EqualTo";
      }
    };

    const clearConditions = () => {
      setFilter1Value("");
      setFilter2Value("");
    };

    useImperativeHandle(ref, () => ({
      clearConditions,
    }));

    return (
      <div className="filter-dialog-container dialog-box-min filter-box">
        <div className="dialog-body nopadding">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <ToggleSwitch
              label1="Basic"
              label2="Adv"
              initialState={toggleSwitch}
              onToggle={setToggleSwitch}
            />
          </div>

          <div className="condition-row" style={{ gap: 3 }}>
            <Dropdown
              id={"formula-one-dropdown"}
              selectedValue={selectedOption1 || "="}
              onChange={handleOption1Change}
              options={formulaOptions}
            />

            <label
              htmlFor="condition-value"
              style={{ fontWeight: "bold", marginLeft: 10 }}
            >
              Numeric Value:
            </label>
            <input
              id="condition-value"
              value={filter1Value}
              style={{ width: "49%" }}
              className="input-field"
              type="text"
              onChange={(e) => handleValue1Change(e.target.value)}
            />
          </div>

          {toggleSwitch && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px 100px",
                  gap: "20px",
                }}
              >
                <label className="custom-radio">
                  <input
                    type="radio"
                    id="radio1"
                    value="AND"
                    checked={selectedRadioOption === "AND"}
                    onChange={handleRadioChange}
                  />
                  <span className="checkmark"></span>AND
                </label>
                <label className="custom-radio">
                  <input
                    type="radio"
                    id="radio2"
                    value="OR"
                    checked={selectedRadioOption === "OR"}
                    onChange={handleRadioChange}
                  />
                  <span className="checkmark"></span>OR
                </label>
              </div>

              <div className="condition-row" style={{ gap: 3 }}>
                <Dropdown
                  id={"formula-two-dropdown"}
                  selectedValue={selectedOption2 || "="}
                  onChange={handleOption2Change}
                  options={formulaOptions}
                />

                <label
                  htmlFor="condition-value"
                  style={{ fontWeight: "bold", marginLeft: 10 }}
                >
                  Numeric Value:
                </label>
                <input
                  id="condition-value"
                  value={filter2Value}
                  style={{ width: "49%" }}
                  className="input-field"
                  type="text"
                  onChange={(e) => handleValue2Change(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  },
);

export default FilterFormula;
